import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  expectedScore, kFactor, K_TABLE, IFR_FLOOR, MIN_GAMES_FOR_RATING,
  RATING_DIFF_CAP, NORM_RULES, INACTIVE_AFTER_MONTHS,
} from '../../utils/ifr';
import {
  PLAYER_TITLES, COACH_TITLES, IFC_WEIGHTS, IFC_BASE, IFC_CAP,
} from '../../config/ifrTitles';
import { BOOTSTRAP_RATING } from '../../utils/ifrData';
import { RATING_CATEGORIES } from '../../config/ratingCategories';

// ── UKURASA WA UWAZI ────────────────────────────────────────────────────
// Hadharani, bila login. Kila kanuni iko hapa. Mtu yeyote anaweza
// kuhesabu mwenyewe na kuthibitisha kwamba namba yake ni sahihi.

export default function RatingSystem() {
  const navigate = useNavigate();
  const [me, setMe] = useState(1500);
  const [opp, setOpp] = useState(1600);
  const [result, setResult] = useState(1);
  const [games, setGames] = useState(40);
  const [age, setAge] = useState(20);

  const exp = expectedScore(Number(me) || 0, Number(opp) || 0);
  const k = kFactor({ rating: Number(me) || 0, gamesRated: Number(games) || 0, age: Number(age) || null });
  const change = k * (Number(result) - exp);
  const after = Math.max(IFR_FLOOR, Math.round((Number(me) || 0) + change));

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        <div onClick={() => navigate('/chess')} style={S.back}>← Chess</div>

        <div style={S.hero}>
          <div style={S.heroLabel}>IYANNA FOUNDATION RATING</div>
          <h1 style={S.h1}>Namba yako inatoka wapi</h1>
          <p style={S.heroText}>
            Kila kanuni ya IFR iko kwenye ukurasa huu. Hakuna sehemu ya siri,
            hakuna pointi za upendeleo. Ukitaka kuhakiki rating yako, unaweza
            kuihesabu mwenyewe hapa chini na ulinganishe.
          </p>
        </div>

        {/* ── MISINGI ── */}
        <Card title="Misingi mitano">
          <Rule n="1" title="Michezo ya ana kwa ana pekee">
            Mchezo unahesabika tu ukichezwa kwenye tukio rasmi lililosajiliwa
            na matokeo yakathibitishwa na arbiter. Michezo ya mtandaoni na ya
            kompyuta haiingii kabisa kwenye IFR — hizo zina rating yake ya
            mazoezi, tofauti kabisa.
          </Rule>
          <Rule n="2" title="Hakuna pointi za bure">
            Huwezi kupanda kwa kuhudhuria, kwa kucheza mara nyingi, wala kwa
            kulipia. Pointi zinatoka kwenye matokeo dhidi ya wapinzani
            waliopimwa — hakuna njia nyingine.
          </Rule>
          <Rule n="3" title="Rating inachapishwa kila mwezi">
            Michezo yote ya mwezi inahesabiwa kwa mkupuo mmoja dhidi ya rating
            ya <strong>mwanzo</strong> wa mwezi, si mchezo baada ya mchezo.
            Hivyo mpangilio wa michezo hauwezi kutumika kujipendelea.
          </Rule>
          <Rule n="4" title={`Sakafu ni ${IFR_FLOOR}`}>
            Rating haiwezi kushuka chini ya {IFR_FLOOR}. Vilevile tofauti
            kubwa kuliko pointi {RATING_DIFF_CAP} kati yako na mpinzani
            inahesabiwa kama {RATING_DIFF_CAP} tu — hii inazuia mkubwa
            kuchuma pointi kwa kucheza na wanaoanza.
          </Rule>
          <Rule n="5" title="Kila mchezo unahifadhiwa milele">
            Michezo yote inabaki kwenye kumbukumbu. Ikitokea hitilafu, rating
            zote za kila mtu zinaweza kuhesabiwa upya kutoka mwanzo kabisa —
            na matokeo yatakuwa yaleyale. Bila hili, mfumo usingeweza kudumu.
          </Rule>
        </Card>

        {/* ── KASI TATU ── */}
        <Card title="Kasi tatu — Classical, Rapid, Blitz">
          <p style={S.p}>
            Kama FIDE, IFR ina orodha <strong>tatu tofauti</strong> za rating —
            moja kwa kila kasi ya mchezo. Hazichanganyiki kamwe: mchezo wa
            Blitz hauguswi rating ya Classical, na kinyume chake.
          </p>
          {RATING_CATEGORIES.map(c => (
            <div key={c.key} style={{ ...S.note, borderLeft: `4px solid ${c.color}`, marginBottom: '11px' }}>
              <strong style={{ color: c.color }}>{c.label}</strong> — {c.description}
            </div>
          ))}
          <p style={{ ...S.p, marginBottom: 0 }}>
            <strong>Titles ni za Classical pekee</strong> — kama FIDE halisi.
            Rapid na Blitz zina namba zake, lakini hazitoi title. Formula na
            K-factor ni sawa kwa kasi zote tatu; tofauti ni michezo tu
            inayohesabiwa.
          </p>
        </Card>

        {/* ── FORMULA ── */}
        <Card title="Formula">
          <div style={S.formula}>
            Rating mpya = Rating ya zamani + K × (Matokeo − Matarajio)
          </div>
          <p style={S.p}>
            <strong>Matarajio</strong> ni nafasi yako ya kushinda kulingana na
            tofauti ya rating. Ikiwa nyinyi wawili mna rating sawa, matarajio
            ni 0.5 — yaani mnatarajiwa kugawana pointi.
          </p>
          <div style={S.formulaSmall}>
            Matarajio = 1 ÷ (1 + 10<sup>(rating ya mpinzani − rating yako) ÷ 400</sup>)
          </div>
          <p style={S.p}>
            <strong>K</strong> inaamua kasi ya kubadilika. Anayeanza anasogea
            haraka kwa sababu rating yake bado haijulikani vizuri; aliyefika
            juu anasogea polepole kwa sababu rating yake tayari ni sahihi.
          </p>
          <table style={S.table}>
            <thead>
              <tr><th style={S.th}>Hali</th><th style={{ ...S.th, textAlign: 'right' }}>K</th></tr>
            </thead>
            <tbody>
              {K_TABLE.map((r, i) => (
                <tr key={i}>
                  <td style={S.td}>{r.when}</td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 800 }}>{r.k}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        {/* ── KIKOKOTOO ── */}
        <Card title="Kikokotoo — jihesabie mwenyewe">
          <div style={S.grid2}>
            <Num label="Rating yako" value={me} onChange={setMe} />
            <Num label="Rating ya mpinzani" value={opp} onChange={setOpp} />
            <Num label="Michezo uliyocheza" value={games} onChange={setGames} />
            <Num label="Umri wako" value={age} onChange={setAge} />
          </div>

          <div style={{ marginTop: '4px', marginBottom: '16px' }}>
            <label style={S.label}>Matokeo</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[{ v: 1, l: 'Ushindi' }, { v: 0.5, l: 'Sare' }, { v: 0, l: 'Kushindwa' }].map(o => (
                <button key={o.v} onClick={() => setResult(o.v)} style={{
                  flex: 1, padding: '11px', borderRadius: '10px', cursor: 'pointer',
                  border: `1px solid ${result === o.v ? '#14452F' : '#E8E4DA'}`,
                  background: result === o.v ? '#14452F' : '#fff',
                  color: result === o.v ? '#fff' : '#6B7280',
                  fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
                }}>{o.l}</button>
              ))}
            </div>
          </div>

          <div style={S.calcOut}>
            <CalcRow label="Matarajio yako" value={exp.toFixed(3)} />
            <CalcRow label="K inayotumika" value={k} />
            <CalcRow label="Hesabu" value={`${k} × (${result} − ${exp.toFixed(3)})`} />
            <CalcRow
              label="Mabadiliko"
              value={`${change >= 0 ? '+' : ''}${change.toFixed(1)}`}
              big color={change >= 0 ? '#059669' : '#DC2626'}
            />
            <CalcRow label="Rating mpya" value={after} big color="#14452F" />
          </div>
          <p style={{ ...S.p, fontSize: '12px', marginBottom: 0 }}>
            Kumbuka: kwenye mfumo halisi, michezo yote ya mwezi inaunganishwa
            kabla ya kuhesabu, hivyo jumla inaweza kutofautiana kidogo na
            mchezo mmoja peke yake.
          </p>
        </Card>

        {/* ── RATING YA KWANZA ── */}
        <Card title="Rating yako ya kwanza">
          <p style={S.p}>
            Huipati kwa kujiandikisha. Unaipata baada ya kucheza michezo{' '}
            <strong>{MIN_GAMES_FOR_RATING}</strong> dhidi ya wachezaji ambao
            tayari wana rating rasmi. Kisha:
          </p>
          <div style={S.formulaSmall}>
            Rating ya kwanza = wastani wa rating za wapinzani + marekebisho ya asilimia yako ya pointi
          </div>
          <p style={S.p}>
            Ukipata 0% kwenye michezo yote, bado hupewi rating — michezo yako
            inahifadhiwa na unaendelea kwenye tukio lijalo. Hakuna anayepewa
            namba asiyoistahili.
          </p>
          <div style={S.note}>
            <strong>Tukio la msingi:</strong> shule ikianza chess kwa mara ya
            kwanza, hakuna mtu mwenye rating — hivyo hakuna wa kupimwa naye.
            Kwa hali hii, admin anatangaza tukio kama "tukio la msingi": kila
            mtu anahesabiwa kama {BOOTSTRAP_RATING} kwa ajili ya hesabu, na
            kila mmoja anapata rating yake ya kwanza kutokana na matokeo ya
            tukio hilo. Baada ya hapo kanuni za kawaida zinarudi. Tukio la
            msingi <strong>haliwezi kutoa norm wala title</strong>.
          </div>
        </Card>

        {/* ── TITLES ── */}
        <Card title="Titles za wachezaji">
          <p style={S.p}>
            Title haiondoki. Rating yako ikishuka baadaye, title inabaki —
            kwa sababu inaelezea ulichowahi kufanya, si ulipo leo.
          </p>
          {PLAYER_TITLES.map(t => (
            <div key={t.code} style={{ ...S.titleRow, borderLeft: `4px solid ${t.color}` }}>
              <div style={{ ...S.titleCode, background: `${t.color}18`, color: t.color }}>{t.code}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.titleName}>{t.name}</div>
                <div style={S.titleSub}>{t.english}</div>
                <div style={S.titleReq}>
                  Rating {t.rating}
                  {t.norms > 0 && ` · norm ${t.norms} (performance ${t.normPerformance}+)`}
                </div>
              </div>
            </div>
          ))}

          <div style={{ ...S.note, marginTop: '18px' }}>
            <strong>Norm ni nini?</strong> Ni ushahidi kwamba ulicheza kwa
            kiwango cha title kwenye tukio <strong>moja</strong> — si kwa
            bahati, si kwa kucheza na wadogo. Ili tukio limkupe norm:
            <ul style={S.ul}>
              <li>Michezo angalau {NORM_RULES.minGames} kwenye tukio hilo</li>
              <li>Wapinzani tofauti angalau {NORM_RULES.minDistinctOpponents}</li>
              <li>Angalau {NORM_RULES.minOtherClubOpponents} kutoka club nyingine</li>
              <li>Wastani wa rating za wapinzani usiwe chini ya (lengo − {NORM_RULES.avgOpponentBelowTarget})</li>
              <li>Performance yako ifikie kiwango cha title husika</li>
            </ul>
            Performance inahesabiwa hivi: wastani wa rating za wapinzani +
            marekebisho ya asilimia ya pointi ulizopata.
          </div>
        </Card>

        {/* ── MAKOCHA ── */}
        <Card title="IFC — rating ya makocha">
          <p style={S.p}>
            Kocha hachezi, hivyo Elo haifai kwake. IFC inapima kile
            kinachoonekana na kuthibitishwa: wanafunzi wangapi amewafikisha
            kwenye rating rasmi, wamepanda kiasi gani chini yake, wangapi
            wamepata title, na amefundisha session ngapi.
          </p>
          <div style={S.formulaSmall}>
            IFC = {IFC_BASE} + jumla ya pointi za vipengele (kikomo {IFC_CAP})
          </div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Kipengele</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Pointi</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Kikomo</th>
              </tr>
            </thead>
            <tbody>
              {IFC_WEIGHTS.map(w => (
                <tr key={w.key}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{w.label}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{w.note}</div>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {w.points} <span style={{ fontSize: '10.5px', color: '#6B7280' }}>/{w.unit.replace('kila ', '')}</span>
                  </td>
                  <td style={{ ...S.td, textAlign: 'right', fontWeight: 700 }}>{w.max}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: '18px' }}>
            {COACH_TITLES.map(t => (
              <div key={t.code} style={{ ...S.titleRow, borderLeft: `4px solid ${t.color}` }}>
                <div style={{ ...S.titleCode, background: `${t.color}18`, color: t.color }}>{t.code}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.titleName}>{t.name}</div>
                  <div style={S.titleSub}>{t.english}</div>
                  <div style={S.titleReq}>{t.description}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* ── UHAI ── */}
        <Card title="Kutokuwa na shughuli">
          <p style={S.p}>
            Usipocheza mchezo rasmi kwa miezi {INACTIVE_AFTER_MONTHS}, unawekwa
            "hana shughuli". Rating yako <strong>haifutwi wala haishuki</strong> —
            inahifadhiwa tu, na inarudi kwenye orodha kuu siku unayocheza tukio
            rasmi tena. Title yako inabaki milele bila kujali muda.
          </p>
        </Card>

        <div style={S.footer}>
          Kanuni hizi zinatumika kwa kila mtu kwa usawa. Zikibadilika,
          mabadiliko yatatangazwa hapa na rating zote zitahesabiwa upya
          chini ya kanuni mpya ili kila mtu apimwe kwa kipimo kimoja.
        </div>

      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={S.card}>
      <h2 style={S.h2}>{title}</h2>
      {children}
    </div>
  );
}

function Rule({ n, title, children }) {
  return (
    <div style={{ display: 'flex', gap: '13px', marginBottom: '18px' }}>
      <div style={S.ruleNum}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: 800, color: '#14452F', marginBottom: '5px' }}>{title}</div>
        <div style={{ fontSize: '13.5px', color: '#4B5563', lineHeight: 1.75 }}>{children}</div>
      </div>
    </div>
  );
}

function Num({ label, value, onChange }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      <input type="number" style={S.input} value={value}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}

function CalcRow({ label, value, big, color }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: big ? '11px 0' : '7px 0',
      borderTop: '1px solid #EFEDE6',
    }}>
      <span style={{ fontSize: '12.5px', color: '#6B7280' }}>{label}</span>
      <span style={{
        fontSize: big ? '19px' : '13.5px', fontWeight: big ? 800 : 600,
        color: color || '#1A1A1A', fontVariantNumeric: 'tabular-nums',
      }}>{value}</span>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#F6F4EE', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: '20px 16px 60px' },
  wrap: { maxWidth: '720px', margin: '0 auto' },
  back: { fontSize: '12.5px', color: '#6B7280', cursor: 'pointer', marginBottom: '14px' },
  hero: {
    background: 'linear-gradient(135deg, #0B3524, #175239)', color: '#fff',
    borderRadius: '20px', padding: '28px 24px', marginBottom: '18px',
  },
  heroLabel: { fontSize: '10.5px', letterSpacing: '1.6px', opacity: 0.7, fontWeight: 800 },
  h1: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '27px', margin: '9px 0 11px', lineHeight: 1.15 },
  heroText: { fontSize: '13.5px', lineHeight: 1.8, opacity: 0.92, margin: 0 },
  card: { background: '#fff', border: '1px solid #E8E4DA', borderRadius: '18px', padding: '22px', marginBottom: '16px' },
  h2: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '19px', color: '#14452F', margin: '0 0 16px' },
  p: { fontSize: '13.5px', color: '#4B5563', lineHeight: 1.8, margin: '0 0 14px' },
  ruleNum: {
    width: '28px', height: '28px', borderRadius: '9px', flexShrink: 0,
    background: '#14452F', color: '#fff', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '13px', fontWeight: 800,
  },
  formula: {
    background: '#0B3524', color: '#fff', padding: '16px', borderRadius: '12px',
    fontSize: '14.5px', fontWeight: 700, textAlign: 'center', marginBottom: '16px',
  },
  formulaSmall: {
    background: '#FAF8F3', border: '1px solid #EFEDE6', padding: '13px', borderRadius: '10px',
    fontSize: '13px', color: '#14452F', textAlign: 'center', marginBottom: '14px', fontWeight: 600,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th: { textAlign: 'left', padding: '9px 6px', fontSize: '11px', color: '#6B7280', fontWeight: 800, borderBottom: '1px solid #E8E4DA' },
  td: { padding: '10px 6px', borderBottom: '1px solid #F3F1EB', color: '#374151' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '14px' },
  label: { display: 'block', fontSize: '12px', fontWeight: 700, color: '#374151', marginBottom: '6px' },
  input: {
    width: '100%', padding: '11px 13px', borderRadius: '10px', border: '1px solid #E8E4DA',
    fontSize: '14px', fontFamily: 'inherit', background: '#FCFBF8', boxSizing: 'border-box',
  },
  calcOut: { background: '#FAF8F3', borderRadius: '12px', padding: '6px 16px 12px', marginBottom: '14px' },
  note: { background: '#FAF8F3', border: '1px solid #EFEDE6', borderRadius: '12px', padding: '15px', fontSize: '12.5px', color: '#4B5563', lineHeight: 1.75 },
  ul: { margin: '9px 0 0', paddingLeft: '18px', lineHeight: 1.9 },
  titleRow: { display: 'flex', gap: '12px', alignItems: 'center', padding: '13px 14px', background: '#FCFBF8', borderRadius: '12px', marginBottom: '9px' },
  titleCode: { width: '46px', height: '38px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 900, flexShrink: 0 },
  titleName: { fontSize: '14px', fontWeight: 800, color: '#1A1A1A' },
  titleSub: { fontSize: '11px', color: '#9CA3AF', marginTop: '1px' },
  titleReq: { fontSize: '11.5px', color: '#6B7280', marginTop: '5px', lineHeight: 1.6 },
  footer: { fontSize: '12px', color: '#6B7280', lineHeight: 1.8, textAlign: 'center', padding: '10px 14px' },
};
